import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { formatCurrency, formatNumber, formatYAxisValue } from './formatters';
import EmptyState from '../EmptyState';

interface CfopPeriodoData {
  cfop: string;
  descricao_cfop?: string;
  valor_total: number;
  documentos: number;
  fornecedores?: number;
  produtos_distintos?: number;
}

interface DrillDownCfopPeriodoChartProps {
  cfops_periodo: CfopPeriodoData[];
  title?: string;
  maxItems?: number;
  height?: number;
  gridSize?: {
    xs?: number;
    md?: number;
  };
  color?: string;
  valueKey?: 'valor_total';
  showEmptyState?: boolean;
}

const DrillDownCfopPeriodoChart: React.FC<DrillDownCfopPeriodoChartProps> = ({
  cfops_periodo,
  title = "Principais CFOPs do Período",
  maxItems = 8,
  height = 400,
  gridSize = { xs: 12, md: 6 },
  color,
  valueKey = 'valor_total',
  showEmptyState = true
}) => {
  const theme = useTheme();

  // Validar se há dados para renderizar
  if (!cfops_periodo || cfops_periodo.length === 0) {
    if (!showEmptyState) {
      return null;
    }
    return (
      <Grid item xs={gridSize.xs} md={gridSize.md}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <EmptyState
              icon="📊"
              title="Sem dados de CFOP"
              description="Não há dados de CFOPs disponíveis para o período selecionado"
              height={height}
            />
          </CardContent>
        </Card>
      </Grid>
    );
  }

  // Filtrar e processar dados válidos
  const cfopsValidos = cfops_periodo.filter(item => {
    const temCfop = item && item.cfop;
    const valorNumerico = Number(item[valueKey]);
    const temValorValido = !isNaN(valorNumerico) && valorNumerico > 0;
    
    return temCfop && temValorValido;
  });

  if (cfopsValidos.length === 0) {
    if (!showEmptyState) {
      return null;
    }
    return (
      <Grid item xs={gridSize.xs} md={gridSize.md}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <EmptyState
              icon="📊"
              title="Sem dados válidos de CFOP"
              description="Os dados de CFOPs encontrados não possuem valores válidos"
              height={height}
            />
          </CardContent>
        </Card>
      </Grid>
    );
  }

  // Preparar dados para o gráfico
  const chartData = cfopsValidos
    .slice(0, maxItems)
    .sort((a, b) => Number(b[valueKey]) - Number(a[valueKey])) // Ordenar por valor em ordem decrescente (maior primeiro)
    .map(item => ({
      cfop: item.cfop,
      'Valor Total': Number(item[valueKey]),
      documentos: item.documentos || 0,
      fornecedores: item.fornecedores || 0,
      produtos_distintos: item.produtos_distintos || 0,
      descricao_cfop: item.descricao_cfop || ''
    }));

  // Definir cor do gráfico
  const chartColor = color || theme.palette.warning.main;

  return (
    <Grid item xs={gridSize.xs} md={gridSize.md}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ height }}>
            <ResponsiveBar
              data={chartData}
              keys={['Valor Total']}
              indexBy="cfop"
              margin={{ top: 20, right: 30, bottom: 60, left: 80 }}
              padding={0.3}
              colors={[chartColor]}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'CFOP',
                legendOffset: 36,
                legendPosition: 'middle'
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Valor (R$)',
                legendOffset: -60,
                legendPosition: 'middle',
                format: formatYAxisValue
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor="#fff"
              valueFormat={formatCurrency}
              tooltip={({ id, value, data }) => (
                <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    CFOP {id}
                  </Typography>
                  {(data as any).descricao_cfop && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {(data as any).descricao_cfop}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formatCurrency(value)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber((data as any).documentos)} documentos
                  </Typography>
                  {(data as any).fornecedores > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatNumber((data as any).fornecedores)} fornecedores
                    </Typography>
                  )}
                  {(data as any).produtos_distintos > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatNumber((data as any).produtos_distintos)} produtos distintos
                    </Typography>
                  )}
                </Box>
              )}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default DrillDownCfopPeriodoChart; 